/**
 * 注册页面
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, GalleryVerticalEnd } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { z } from "zod";

import { useSignupMutation } from "@/modules/auth";
import { markOnboardingPending } from "@/modules/onboarding/state";
import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/shared/ui/field";
import { Input } from "@/shared/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/shared/ui/input-group";
import { Spinner } from "@/shared/ui/spinner";

const SignupPage = () => {
  const { t } = useTranslation("signup");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const signupMutation = useSignupMutation();

  const formSchema = z
    .object({
      email: z.string().email({
        message: t("validation.email"),
      }),
      password: z.string().min(6, {
        message: t("validation.password"),
      }),
      confirmPassword: z.string().min(1, {
        message: t("validation.confirmPassword"),
      }),
      agreeToTerms: z.literal(true, {
        errorMap: () => ({ message: t("validation.agreeToTerms") }),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: t("validation.confirmPasswordNotMatch"),
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: true,
    },
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  function onSubmit(values: z.infer<typeof formSchema>) {
    signupMutation.mutate(
      {
        password: values.password,
        email: values.email,
      },
      {
        onSuccess: (response) => {
          form.reset();
          markOnboardingPending(response.token);
          toast.success(t("signupSuccess"));
          navigate(ROUTE_PATHS.ONBOARDING, { replace: true });
        },
      }
    );
  }

  return (
    <div className="auth-form flex w-full max-w-md flex-col gap-6">
        <a href={ROUTE_PATHS.HOME} className="flex items-center gap-2 self-center font-medium">
          <div className="bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-md">
            <GalleryVerticalEnd className="size-4" />
          </div>
          Pinbox
        </a>
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="px-8 text-center">
              <CardTitle className="text-xl">{t("title")}</CardTitle>
              <CardDescription>{t("description")}</CardDescription>
            </CardHeader>
            <CardContent className="px-8">
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <FieldGroup>
                  <Controller
                    name="email"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor="email">
                          {t("emailLabel")}
                        </FieldLabel>
                        <Input
                          {...field}
                          className="h-11"
                          aria-invalid={fieldState.invalid}
                          id="email"
                          type="email"
                          placeholder={t("emailPlaceholder")}
                          autoComplete="off"
                        />
                        <FieldDescription>
                          {t("emailDescription")}
                        </FieldDescription>
                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    )}
                  />

                  <Controller
                    name="password"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor="password">
                          {t("passwordLabel")}
                        </FieldLabel>
                        <InputGroup className="h-11">
                          <InputGroupInput
                            {...field}
                            className="h-full"
                            aria-invalid={fieldState.invalid}
                            id="password"
                            type={showPassword ? "text" : "password"}
                            placeholder={t("passwordPlaceholder")}
                            autoComplete="off"
                          />
                          <InputGroupAddon align="inline-end">
                            <InputGroupButton
                              aria-label="Eye"
                              title="toggle password visibility"
                              size="icon-xs"
                              onClick={togglePasswordVisibility}
                              type="button"
                            >
                              {showPassword ? (
                                <Eye className="h-4 w-4" />
                              ) : (
                                <EyeOff className="h-4 w-4" />
                              )}
                            </InputGroupButton>
                          </InputGroupAddon>
                        </InputGroup>
                        <FieldDescription>
                          {t("passwordDescription")}
                        </FieldDescription>
                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    )}
                  />

                  <Controller
                    name="confirmPassword"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor="confirmPassword">
                          {t("confirmPasswordLabel")}
                        </FieldLabel>
                        <InputGroup className="h-11">
                          <InputGroupInput
                            {...field}
                            className="h-full"
                            aria-invalid={fieldState.invalid}
                            id="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder={t("confirmPasswordPlaceholder")}
                            autoComplete="off"
                          />
                          <InputGroupAddon align="inline-end">
                            <InputGroupButton
                              aria-label="Eye"
                              title="toggle password visibility"
                              size="icon-xs"
                              onClick={toggleConfirmPasswordVisibility}
                              type="button"
                            >
                              {showConfirmPassword ? (
                                <Eye className="h-4 w-4" />
                              ) : (
                                <EyeOff className="h-4 w-4" />
                              )}
                            </InputGroupButton>
                          </InputGroupAddon>
                        </InputGroup>
                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    )}
                  />

                  <Field>
                    <Button
                      type="submit"
                      className="h-11"
                      disabled={signupMutation.isPending}
                    >
                      {signupMutation.isPending && <Spinner />}
                      {t("submitButton")}
                    </Button>
                    <FieldDescription className="text-center">
                      {t("noAccount")}{" "}
                      <Link className="font-medium" to={ROUTE_PATHS.LOGIN}>
                        {" "}
                        {t("login")}
                      </Link>
                    </FieldDescription>
                  </Field>
                </FieldGroup>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
  );
};

export default SignupPage;
